export const CAMPAIGN_TYPES = [
  'promotion',
  'announcement',
  'upgrade',
  'renewal',
  'informational',
  'feature_update',
  'maintenance',
  'custom',
] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

export const CAMPAIGN_CHANNELS = [
  'portal',
  'mobile_app',
  'push_notification',
  'whatsapp',
  'email',
] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

export const CAMPAIGN_PLACEMENTS = ['dialog_box', 'top_banner', 'bottom_right'] as const;
export type CampaignPlacement = (typeof CAMPAIGN_PLACEMENTS)[number];

export interface CampaignChannelFields {
  placements: readonly CampaignPlacement[];
  format: boolean;
  formats: readonly CampaignFormat[];
  message: boolean;
  buttons: boolean;
  template: boolean;
}

export const CHANNEL_FIELDS: Record<CampaignChannel, CampaignChannelFields> = {
  portal: {
    placements: ['dialog_box', 'top_banner', 'bottom_right'],
    format: true,
    formats: ['text', 'image', 'video'],
    message: true,
    buttons: true,
    template: false,
  },
  mobile_app: {
    placements: ['dialog_box'],
    format: true,
    formats: ['text', 'image', 'video'],
    message: true,
    buttons: true,
    template: false,
  },
  push_notification: {
    placements: [],
    format: true,
    formats: ['text', 'image'],
    message: true,
    buttons: false,
    template: false,
  },
  whatsapp: {
    placements: [],
    format: false,
    formats: ['text'],
    message: false,
    buttons: false,
    template: true,
  },
  email: {
    placements: [],
    format: true,
    formats: ['text', 'image'],
    message: true,
    buttons: false,
    template: false,
  },
};

const UNKNOWN_CHANNEL_FIELDS: CampaignChannelFields = {
  placements: [],
  format: true,
  formats: ['text', 'image', 'video'],
  message: true,
  buttons: false,
  template: false,
};

export function channelFields(channel: CampaignChannel | string): CampaignChannelFields {
  return CHANNEL_FIELDS[channel as CampaignChannel] || UNKNOWN_CHANNEL_FIELDS;
}

export function placementsForChannel(
  channel: CampaignChannel | string,
): readonly CampaignPlacement[] {
  return channelFields(channel).placements;
}

export function channelAllowsPlacement(
  channel: CampaignChannel | string,
  placement: CampaignPlacement | string,
): boolean {
  const allowed = placementsForChannel(channel);
  if (allowed.length === 0) return true;
  return (allowed as readonly string[]).includes(placement);
}

export function coercePlacementForChannel(
  channel: CampaignChannel | string,
  placement: CampaignPlacement,
): CampaignPlacement {
  if (channelAllowsPlacement(channel, placement)) return placement;
  return placementsForChannel(channel)[0] || placement;
}

export const CAMPAIGN_FORMATS = ['text', 'image', 'video'] as const;
export type CampaignFormat = (typeof CAMPAIGN_FORMATS)[number];

export function formatsForChannel(
  channel: CampaignChannel | string,
): readonly CampaignFormat[] {
  return channelFields(channel).formats;
}

export function channelAllowsFormat(
  channel: CampaignChannel | string,
  format: CampaignFormat | string,
): boolean {
  return (formatsForChannel(channel) as readonly string[]).includes(format);
}

export function coerceFormatForChannel(
  channel: CampaignChannel | string,
  format: CampaignFormat,
): CampaignFormat {
  if (channelAllowsFormat(channel, format)) return format;
  return formatsForChannel(channel)[0] || 'text';
}

export const CAMPAIGN_BODY_FORMATS = ['text', 'html'] as const;
export type CampaignBodyFormat = (typeof CAMPAIGN_BODY_FORMATS)[number];

export const CAMPAIGN_AUDIENCE_MODES = ['all', 'plans'] as const;
export type CampaignAudienceMode = (typeof CAMPAIGN_AUDIENCE_MODES)[number];

export const CAMPAIGN_STORED_STATUSES = [
  'draft',
  'scheduled',
  'published',
  'paused',
  'inactive',
] as const;
export type CampaignStoredStatus = (typeof CAMPAIGN_STORED_STATUSES)[number];

export const CAMPAIGN_STATUSES = [
  'draft',
  'scheduled',
  'active',
  'paused',
  'expired',
  'inactive',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const MAX_CAMPAIGN_BUTTONS = 2;
export const MAX_CAMPAIGN_TYPE_LABEL_LENGTH = 40;
export const MAX_CAMPAIGN_TITLE_LENGTH = 255;

export type CampaignDraftSeed = {
  channel?: CampaignChannel;
  type?: CampaignType;
  typeLabel?: string;
  placement?: CampaignPlacement;
  title?: string;
  message?: string;
  audienceMode?: CampaignAudienceMode;
};

export interface CampaignButton {
  label: string;
  url: string;
}

export interface CampaignPlanRef {
  id: string;
  name: string | null;
  billingPeriod: string | null;
  label: string;
}

export interface MessageCampaign {
  id: string;
  title: string;
  message: string;
  bodyFormat: CampaignBodyFormat;
  type: CampaignType;
  typeLabel: string | null;
  channel: CampaignChannel;
  placement: CampaignPlacement;
  templateName: string | null;
  templateLanguage: string | null;
  templateVariables: Record<string, string> | null;
  format: CampaignFormat;
  mediaUrl: string | null;
  audienceMode: CampaignAudienceMode;
  planIds: string[];
  plans: CampaignPlanRef[];
  buttons: CampaignButton[];
  storedStatus: CampaignStoredStatus;
  status: CampaignStatus;
  publishAt: string | null;
  expiresAt: string | null;
  impressions: number;
  clicks: number;
  videoPlays: number;
  ctr: number | null;
  createdBy: string;
  createdByEmail: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CampaignPlan {
  id: string;
  name: string;
  slug: string;
  billingPeriod: string | null;
  label: string;
  price: string | number | null;
  currency: string | null;
  isActive: boolean;
}

export interface CampaignAudienceWorkspace {
  organizationId: string;
  organizationName: string;
  planLabel: string;
  adminName: string | null;
  adminEmail: string | null;
  subscriptionEndsAt: string | null;
}

export interface CampaignAudiencePreview {
  workspaces: CampaignAudienceWorkspace[];
  truncated: boolean;
  limit: number;
}

export interface CampaignAnalyticsRow {
  id: string;
  title: string;
  status: CampaignStatus;
  type: CampaignType;
  typeLabel: string | null;
  channel: CampaignChannel;
  format: CampaignFormat;
  audienceMode: CampaignAudienceMode;
  plans: CampaignPlanRef[];
  views: number;
  ctaClicks: number;
  dismissed: number;
  videoPlays: number;
  ctr: number | null;
  createdAt: string | null;
}

export interface CampaignAnalytics {
  totals: {
    views: number;
    ctaClicks: number;
    dismissed: number;
    videoPlays: number;
    ctr: number | null;
    campaigns: number;
  };
  campaigns: CampaignAnalyticsRow[];
  truncated: boolean;
}

export interface CampaignOverview {
  campaigns: {
    total: number;
    draft: number;
    scheduled: number;
    active: number;
    paused: number;
    expired: number;
    inactive: number;
  };
  subscribers: number;
  subscribersByPlan: Array<{ plan: string; subscribers: number }>;
  subscribersByPlanId: Record<string, number>;
  impressions: number;
  clicks: number;
  ctr: number | null;
}

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  promotion: 'Promotion',
  announcement: 'Announcement',
  upgrade: 'Upgrade',
  renewal: 'Renewal',
  informational: 'Informational',
  feature_update: 'Feature Update',
  maintenance: 'Maintenance',
  custom: 'Custom',
};

export function campaignTypeLabel(campaign: {
  type: CampaignType;
  typeLabel?: string | null;
}): string {
  const custom = campaign.typeLabel?.trim();
  if (campaign.type === 'custom' && custom) return custom;
  return CAMPAIGN_TYPE_LABELS[campaign.type] || campaign.type;
}

export const CAMPAIGN_CHANNEL_LABELS: Record<CampaignChannel, string> = {
  portal: 'Portal',
  mobile_app: 'Mobile app',
  push_notification: 'Push notification',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

export const CAMPAIGN_PLACEMENT_LABELS: Record<CampaignPlacement, string> = {
  dialog_box: 'Dialog Box',
  top_banner: 'Top Banner',
  bottom_right: 'Bottom-Right Notification',
};

export const CAMPAIGN_PLACEMENT_HINTS: Record<CampaignPlacement, string> = {
  dialog_box: 'Centered modal. Text, image, or video.',
  top_banner: 'Bar under the topbar. Text only — no image or video.',
  bottom_right: 'Corner card. Text, image, or video.',
};

export const CAMPAIGN_FORMAT_LABELS: Record<CampaignFormat, string> = {
  text: 'Text',
  image: 'Image',
  video: 'Video',
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Active',
  paused: 'Paused',
  expired: 'Expired',
  inactive: 'Inactive',
};

export const CAMPAIGN_STATUS_BADGE_CLASSES: Record<CampaignStatus, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  scheduled: 'bg-blue-50 text-blue-600 border-blue-200',
  active: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  paused: 'bg-amber-50 text-amber-700 border-amber-200',
  expired: 'bg-red-50 text-red-600 border-red-200',
  inactive: 'bg-slate-100 text-slate-500 border-slate-200',
};

export const CAMPAIGN_STATUS_DOT_CLASSES: Record<CampaignStatus, string> = {
  draft: 'bg-slate-400',
  scheduled: 'bg-blue-500',
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  expired: 'bg-red-500',
  inactive: 'bg-slate-300',
};

export const CAMPAIGN_BUTTON_DESTINATIONS = [
  { value: '/home/account-settings?section=plans&interval=monthly', label: 'Plans & subscriptions' },
  { value: '/home/billing', label: 'Billing' },
  { value: '/home', label: 'Dashboard' },
  { value: '/home/channels/whatsapp', label: 'Inbox' },
  { value: '/home/boards/leads', label: 'CRM' },
  { value: '/home/new-templates', label: 'Templates' },
  { value: '/home/new-campaigns', label: 'Campaigns' },
  { value: '/home/integrations', label: 'Integrations' },
  { value: '/home/account-settings', label: 'Settings' },
  { value: '/home/workspace-settings', label: 'Workspace settings' },
  { value: '/home/updates', label: 'Updates' },
] as const;

export const CAMPAIGN_BUTTON_CUSTOM_URL = '__custom__';

export function isPresetDestination(url: string): boolean {
  return CAMPAIGN_BUTTON_DESTINATIONS.some((option) => option.value === url);
}

export const CAMPAIGN_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

export function normalizeCampaignCycle(billingPeriod: string | null | undefined): string {
  const value = (billingPeriod || '').toLowerCase();
  return value === 'annual' ? 'yearly' : value;
}

export type PlanMatrixPlan = Pick<CampaignPlan, 'id' | 'billingPeriod' | 'label'> & {
  name: string | null;
};

export interface PlanMatrixRow {
  name: string;
  cells: Record<string, PlanMatrixPlan[] | undefined>;
}

export function buildPlanMatrix(
  plans: readonly PlanMatrixPlan[],
  options: { extraPlans?: readonly PlanMatrixPlan[] } = {},
): {
  cycles: string[];
  rows: PlanMatrixRow[];
} {
  const seen = new Set<string>();
  const included: PlanMatrixPlan[] = [];
  for (const plan of [...plans, ...(options.extraPlans || [])]) {
    if (!plan?.id || seen.has(plan.id)) continue;
    seen.add(plan.id);
    included.push(plan);
  }

  const standardCycles = CAMPAIGN_CYCLES.map((cycle) => cycle.value as string);
  const presentCycles = new Set(included.map((plan) => normalizeCampaignCycle(plan.billingPeriod)));
  const cycles = [
    ...standardCycles.filter((cycle) => presentCycles.has(cycle)),
    ...[...presentCycles]
      .filter((cycle) => !standardCycles.includes(cycle))
      .sort((a, b) => a.localeCompare(b)),
  ];

  const rows: PlanMatrixRow[] = [];
  const rowByName = new Map<string, number>();
  for (const plan of included) {
    const name = (plan.name || '').trim() || (plan.label || '').trim() || 'Unknown plan';
    const key = name.toLowerCase();
    let index = rowByName.get(key);
    if (index === undefined) {
      index = rows.length;
      rowByName.set(key, index);
      rows.push({ name, cells: {} });
    }
    const cycle = normalizeCampaignCycle(plan.billingPeriod);
    const cell = rows[index].cells[cycle] || (rows[index].cells[cycle] = []);
    cell.push(plan);
  }

  return { cycles, rows };
}


export function cycleLabel(billingPeriod: string | null | undefined): string {
  const period = (billingPeriod || '').toLowerCase();
  if (!period) return '';
  const titled = period.charAt(0).toUpperCase() + period.slice(1);
  return titled === 'Annual' ? 'Yearly' : titled;
}

export function planLabel(name: string | null | undefined, billingPeriod: string | null | undefined): string {
  const plan = (name || 'Plan').trim();
  const cycle = cycleLabel(billingPeriod);
  return cycle ? `${plan} ${cycle}` : plan;
}

export function planNames(plans: CampaignPlan[]): string[] {
  return [...new Set(plans.map((plan) => plan.name).filter(Boolean))];
}

export function audienceChips(
  campaign: Pick<MessageCampaign, 'audienceMode' | 'plans'>,
  max = 2
): { chips: string[]; overflow: number; hidden: string[] } {
  if (campaign.audienceMode === 'all') {
    return { chips: ['All Users'], overflow: 0, hidden: [] };
  }
  const labels = (Array.isArray(campaign.plans) ? campaign.plans : [])
    .map((plan) => plan?.label)
    .filter((label): label is string => !!label);
  return {
    chips: labels.slice(0, max),
    overflow: Math.max(labels.length - max, 0),
    hidden: labels.slice(max),
  };
}

export function channelHasPlacement(channel: CampaignChannel | string): boolean {
  return placementsForChannel(channel).length > 0;
}

export const CAMPAIGN_SEND_CHANNELS: CampaignChannel[] = [
  'push_notification',
  'whatsapp',
  'email',
];

export function isOneTimeSendChannel(channel: CampaignChannel | string): boolean {
  return (CAMPAIGN_SEND_CHANNELS as string[]).includes(channel);
}

export const CAMPAIGN_DELIVERY_NOTES: Record<CampaignChannel, string> = {
  portal:
    'Shown in the web app on every login and reload while the campaign is live, until the reader dismisses it.',
  mobile_app:
    'Shown in the mobile app on every launch while the campaign is live, until the reader dismisses it.',
  push_notification:
    'Sent once, at the scheduled time, to every user in the audience who has a registered device.',
  whatsapp:
    'Sent once, at the scheduled time, as a WhatsApp message to each workspace owner in the audience.',
  email:
    'Sent once, at the scheduled time, to the email address of each workspace owner in the audience.',
};

export function placementAllowsMedia(placement: CampaignPlacement | string): boolean {
  return placement !== 'top_banner';
}

export function channelLabel(campaign: Pick<MessageCampaign, 'channel' | 'placement'>): string {
  const channel = CAMPAIGN_CHANNEL_LABELS[campaign.channel] || campaign.channel;
  if (!channelHasPlacement(campaign.channel)) return channel;
  const placement = CAMPAIGN_PLACEMENT_LABELS[campaign.placement] || campaign.placement;
  return `${channel} · ${placement}`;
}

export function formatCampaignDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function campaignPreviewText(campaign: Pick<MessageCampaign, 'message' | 'bodyFormat'>, max = 90): string {
  const raw = campaign.message || '';
  const text = campaign.bodyFormat === 'html' ? raw.replace(/<[^>]*>/g, ' ') : raw;
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > max ? `${collapsed.slice(0, max).trimEnd()}...` : collapsed;
}

export function canPause(status: CampaignStatus): boolean {
  return status === 'active';
}

export function canResume(status: CampaignStatus): boolean {
  return status === 'paused';
}
